import {
    RefreshCw,
    ClipboardList,
    Search,
    Sparkles,
    Phone,
    ArrowUpRight,
    Building2,
    Home as HomeIcon,
    Briefcase,
    Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

const AUDIENCES = [
    { icon: Building2, title: "Property Managers", desc: "Single-family, multi-family, and small portfolios." },
    { icon: HomeIcon, title: "HOAs", desc: "Common-area maintenance and member work orders." },
    { icon: Briefcase, title: "Realtors", desc: "Pre-listing punch lists and post-inspection repairs." },
];

const SERVICES = [
    {
        key: "turnover",
        icon: RefreshCw,
        title: "Turnover Maintenance",
        lede: "Get the unit rent-ready, fast.",
        body:
            "5 Star is your go-to crew for fast turnover services built for property managers. The moment a tenant moves out, we move in — patching drywall, replacing damaged doors, chasing down leaks, swapping window coverings, rebuilding decks, and refreshing bathroom finishes. The unit's ready to list again before you've finished filing the move-out report.",
        bullets: ["Drywall patch & paint", "Door & lock replacement", "Leak repairs", "Window coverings", "Deck rebuilds", "Bathroom refresh"],
    },
    {
        key: "service-orders",
        icon: ClipboardList,
        title: "Tenant Service Orders & Requests",
        lede: "Work orders handled. You stay informed.",
        body:
            "Managing dozens of rentals means a steady flow of work orders. Our trained techs coordinate directly with tenants to schedule the visit, complete the repair on time, and send you a detailed invoice by email when the job's wrapped. You'll get progress updates throughout the day — so you know your portfolio's being prioritized, no chasing required.",
        bullets: ["Direct tenant scheduling", "Same-day status updates", "Detailed emailed invoices", "One point of contact"],
    },
    {
        key: "inspection",
        icon: Search,
        title: "Inspection Repairs",
        lede: "Knock out the punch list before it becomes a project.",
        body:
            "Property inspections that surface small issues are actually good news — owners would rather handle the punch list now than face major repairs later. 5 Star tackles the small stuff: caulking, leaky faucets, squeaky doors, missing door stops. Catching these early keeps them from snowballing into serious construction problems.",
        bullets: ["Pre-listing punch lists", "Buyer-inspection repairs", "Caulk, seals, & finishes", "Hardware & door stops"],
    },
    {
        key: "curb-appeal",
        icon: Sparkles,
        title: "Curb Appeal",
        lede: "Make the property the one people want.",
        body:
            "How a property looks matters. Leaning fences, broken windows, and visible neglect chase off good tenants and signal an unsafe environment. We help your property stand out for the right reasons — clean, sharp, well-kept, and ready to attract the kind of residents you actually want.",
        bullets: ["Fence repair & straightening", "Window & screen repair", "Exterior touch-up paint", "Landscape light fixes"],
    },
];

const STATS = [
    { n: "25+", l: "Years in the trade" },
    { n: "24/7", l: "Emergency response" },
    { n: "1", l: "Crew · One point of contact" },
];

export default function PropertyManagers() {
    useSeo({
        title: "Property Manager, HOA & Realtor Handyman Services · Medford, Oregon | 5 Star Construction",
        description: "Turnover, work-order, and ongoing maintenance services for property managers, HOAs, and realtors across Medford, Ashland, and the Rogue Valley. Single point of contact, fast response, licensed Oregon GC (CCB #232477). Call 541-292-9620.",
        path: "/managers",
        jsonLdKey: "managers",
        jsonLd: [
            serviceSchema({
                name: "Property Manager, HOA & Realtor Maintenance Services",
                description: "Turnover punch-list work, ongoing maintenance, on-demand repairs, and pre-listing prep for property managers, HOAs, and realtors in Medford and Southern Oregon.",
                path: "/managers",
                serviceType: "Property Maintenance Contractor",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "For Pros", path: "/managers" },
            ]),
        ],
    });
    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="inline-flex items-center gap-2 mb-6 border border-[#E11D2A] px-3 py-1.5">
                        <span className="w-1.5 h-1.5 bg-[#E11D2A]" />
                        <span className="font-['Manrope'] text-xs font-bold tracking-[0.22em] uppercase text-[#E11D2A]">For property pros</span>
                    </div>
                    <h1 className="font-['Anton'] tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl uppercase max-w-4xl">
                        One trusted crew for{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10 text-[#E11D2A]">your whole portfolio.</span>
                        </span>
                    </h1>
                    <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-3xl">
                        Property managers, HOAs, and realtors across Southern Oregon and SW Washington trust 5 Star for turnovers, work orders, inspection repairs, and curb-appeal upgrades. Honest pricing, clean communication, and 25 years on the tools.
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-4">
                        <a href={SITE.phoneHref} data-testid="pm-call-cta">
                            <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" data-testid="pm-quote-cta">
                            <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                START A WORK ORDER
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>

                    {/* Stats */}
                    <div className="mt-14 grid sm:grid-cols-3 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {STATS.map((s) => (
                            <div key={s.l} className="bg-[#111111] p-7">
                                <div className="font-['Anton'] text-5xl text-[#E11D2A] tracking-tight">{s.n}</div>
                                <div className="font-['Manrope'] text-xs uppercase tracking-[0.2em] font-bold mt-2 text-[#F6F5F2]/80">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Audience pills */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14">
                    <div className="grid md:grid-cols-3 gap-5">
                        {AUDIENCES.map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="bg-white border border-[#E2DFD8] p-6 flex items-start gap-4 hover:shadow-block-sm hover:border-[#111111] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all">
                                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD400] border border-[#111111]">
                                    <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2.25} />
                                </div>
                                <div>
                                    <div className="font-display text-xl font-extrabold text-[#111812]">{title}</div>
                                    <div className="font-['Manrope'] text-sm text-[#5F5F5F] mt-1">{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-14">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">What we handle</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                Built for the way you actually work.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Whether you&apos;re flipping a unit between tenants, knocking out an inspection list before close, or keeping HOA common areas sharp — we work the way you need us to. Quick to schedule, clean on the job, and clear on what we did and what it cost.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {SERVICES.map(({ icon: Icon, ...s }) => (
                            <article key={s.key} className="relative bg-white border border-[#E2DFD8] p-7 md:p-9 hover:shadow-block-sm hover:border-[#111111] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all" data-testid={`pm-service-${s.key}`}>
                                <div className="flex items-start justify-between gap-4 mb-6">
                                    <div className="w-14 h-14 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center">
                                        <Icon className="w-6 h-6" strokeWidth={2.25} />
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#5F5F5F]">{`0${SERVICES.indexOf(SERVICES.find((x) => x.key === s.key)) + 1}`}</div>
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#111812]">{s.title}</h3>
                                <p className="mt-3 font-['Manrope'] text-base font-semibold text-[#E11D2A]">{s.lede}</p>
                                <p className="mt-4 font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">{s.body}</p>
                                <ul className="mt-6 grid sm:grid-cols-2 gap-x-4 gap-y-2 pt-6 border-t border-[#E2DFD8]">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-sm font-semibold text-[#111812]">
                                            <Check className="w-4 h-4 text-[#E11D2A] mt-0.5 shrink-0" strokeWidth={3} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-5xl sm:text-6xl">
                            Ready to add a reliable crew to your roster?
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-lg text-[#111111]/85 max-w-2xl">
                            Call us, drop a work order, or reach out about a portfolio agreement. We&apos;ll get back to you within one business day — same day if it&apos;s urgent.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-4">
                        <a href={SITE.phoneHref} data-testid="pm-cta-call">
                            <Button className="rounded-none w-full h-16 px-7 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-lg font-['Manrope']">
                                <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" data-testid="pm-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-16 px-7 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-lg font-['Manrope']">
                                SEND A WORK ORDER
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
